import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { toast } from 'react-toastify';
import {
  apiGetReferralParticipants,
  apiUpdateGuardianDetails,
} from '../../utils/rmsApi';
import { PARTICIPANT_STATUS } from '../../../constants/participantStatuses';

/**
 * RmsEditGuardianDialog
 * ---------------------
 * Staff-side editing of the nominated parent/carer's details (B13 / RMS-59) —
 * the RMS counterpart of the Assessment platform's ProviderEditPatientForm,
 * scoped to the guardian participant. Org Admin / Support only (the caller
 * gates the trigger; the backend enforces it).
 *
 * Lifecycle rules mirror the school-side nomination editing:
 *   - pending guardian (nominated / invite sent): name, phone, relationship
 *     and email are editable. A changed email supersedes the nomination and
 *     sends a fresh invite to the new address — the old sign-in link stops
 *     working.
 *   - account linked: locked — the parent/carer manages their own details.
 *
 * Props: { isOpen, onClose, orgId, patientRecordId, onSaved }
 * onSaved fires after a successful update so the caller can refetch names.
 */

const RELATIONSHIP_OPTIONS = [
  { id: 'parent', label: 'Parent' },
  { id: 'step_parent', label: 'Step-parent' },
  { id: 'foster_carer', label: 'Foster carer' },
  { id: 'kinship_carer', label: 'Kinship carer' },
  { id: 'legal_guardian', label: 'Legal guardian' },
  { id: 'informal_carer', label: 'Informal carer' },
];

const isValidEmail = (value) => {
  const val = (value || '').trim();
  if (!val || val.length > 254) return false;
  if ((val.match(/@/g) || []).length !== 1) return false;
  if (val.includes('..')) return false;
  const [local, domain] = val.split('@');
  if (!local || !domain || local.endsWith('.') || domain.startsWith('.')) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
};

const FIELD_CLASSES =
  'mt-1 block w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm '
  + 'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';

export default function RmsEditGuardianDialog({
  isOpen,
  onClose,
  orgId,
  patientRecordId,
  onSaved,
}) {
  // 'loading' | 'none' | 'locked' | 'form' | 'error'
  const [state, setState] = useState('loading');
  const [participant, setParticipant] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [relationship, setRelationship] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);

  // Load the active guardian on open, prefill the form from the live doc.
  useEffect(() => {
    if (!isOpen || !orgId || !patientRecordId) return;
    let cancelled = false;
    setState('loading');
    (async () => {
      try {
        const { participants } = await apiGetReferralParticipants({ orgId, patientRecordId });
        if (cancelled) return;
        const guardian = (participants || []).find(
          (p) => p.role === 'guardian' && p.status !== PARTICIPANT_STATUS.SUPERSEDED,
        );
        if (!guardian) {
          setState('none');
          return;
        }
        setParticipant(guardian);
        if (guardian.status === PARTICIPANT_STATUS.ACCOUNT_LINKED) {
          setState('locked');
          return;
        }
        setFirstName(guardian.firstName || '');
        setLastName(guardian.lastName || '');
        setPhone(guardian.phone || '');
        setRelationship(guardian.relationship || '');
        setEmail(guardian.email || '');
        setState('form');
      } catch (err) {
        if (!cancelled) setState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, orgId, patientRecordId]);

  const emailChanged =
    (email || '').trim().toLowerCase() !== (participant?.email || '').trim().toLowerCase();
  const canSave =
    !saving && firstName.trim() && lastName.trim() && isValidEmail(email);

  const handleSave = async () => {
    if (!canSave || !participant) return;
    setSaving(true);
    try {
      const result = await apiUpdateGuardianDetails({
        orgId,
        patientRecordId,
        participantId: participant.id,
        updates: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          phone: phone.trim(),
          relationship,
          email: email.trim(),
        },
      });
      toast.success(
        result?.emailChanged
          ? 'Details updated. A new invite has been sent to the new email address.'
          : 'Parent/carer details updated',
      );
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Could not update parent/carer details');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm z-50" />
        <Dialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-md bg-white rounded-xl shadow-lg z-50 overflow-hidden">
          <div className="p-6 text-left">
            <Dialog.Title className="text-base font-semibold text-gray-900">
              Edit parent/carer details
            </Dialog.Title>

            {state === 'loading' && (
              <p className="mt-3 text-sm text-gray-500">Loading the nominated parent/carer…</p>
            )}

            {state === 'none' && (
              <p className="mt-3 text-sm text-gray-600">
                No parent/carer has been nominated on this referral yet.
              </p>
            )}

            {state === 'error' && (
              <p className="mt-3 text-sm text-rose-700">
                Could not load the parent/carer. Close this dialog and try again.
              </p>
            )}

            {state === 'locked' && (
              <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
                <p className="text-sm text-blue-900">
                  {`${participant?.firstName || ''} ${participant?.lastName || ''}`.trim() || 'This parent/carer'}
                  {' '}has set up their account, so they now manage their own details.
                  Staff editing is no longer available.
                </p>
              </div>
            )}

            {state === 'form' && (
              <div className="mt-4 space-y-3">
                <Dialog.Description className="text-xs text-gray-500">
                  These details prefill the parent/carer&rsquo;s forms and address their
                  invite. They can be corrected until the parent/carer sets up
                  their account.
                </Dialog.Description>
                <label className="block text-sm font-medium text-gray-900">
                  First name
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className={FIELD_CLASSES}
                  />
                </label>
                <label className="block text-sm font-medium text-gray-900">
                  Last name
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className={FIELD_CLASSES}
                  />
                </label>
                <label className="block text-sm font-medium text-gray-900">
                  Relationship to the child
                  <select
                    value={relationship}
                    onChange={(e) => setRelationship(e.target.value)}
                    className={FIELD_CLASSES}
                  >
                    <option value="">Not recorded</option>
                    {RELATIONSHIP_OPTIONS.map((option) => (
                      <option key={option.id} value={option.id}>{option.label}</option>
                    ))}
                  </select>
                </label>
                <label className="block text-sm font-medium text-gray-900">
                  Phone (optional)
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className={FIELD_CLASSES}
                  />
                </label>
                <label className="block text-sm font-medium text-gray-900">
                  Email
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={FIELD_CLASSES}
                  />
                </label>
                {emailChanged && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <p className="text-xs text-amber-800">
                      Changing the email sends a new invite to the new address and
                      deactivates the previous sign-in link.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
              >
                {state === 'form' ? 'Cancel' : 'Close'}
              </button>
              {state === 'form' && (
                <button
                  type="button"
                  disabled={!canSave}
                  onClick={handleSave}
                  className="px-5 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              )}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
